import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type TextButtonIntent = "primary" | "neutral" | "danger" | "success";
export type TextButtonSize   = "sm" | "md" | "lg";

export interface TextButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Semantic color intent
   * @default "primary"
   */
  intent?: TextButtonIntent;

  /** Size — sm: 12px · md: 14px · lg: 16px
   * @default "md"
   */
  size?: TextButtonSize;

  /** Icon rendered before the label */
  iconLeft?: React.ReactNode;

  /** Icon rendered after the label */
  iconRight?: React.ReactNode;

  /** Shows a spinner and disables interaction */
  loading?: boolean;
}

// ─── Maps ─────────────────────────────────────────────────────────────────────

const intentStyles: Record<TextButtonIntent, React.CSSProperties & { "--hover-bg": string; "--hover-color": string; "--active-bg": string }> = {
  primary: {
    color: "var(--tbtn-primary)",
    "--hover-bg":    "var(--tbtn-primary-bg)",
    "--hover-color": "var(--tbtn-primary-h)",
    "--active-bg":   "var(--tbtn-primary-act)",
  },
  neutral: {
    color: "var(--tbtn-neutral)",
    "--hover-bg":    "var(--tbtn-neutral-bg)",
    "--hover-color": "var(--tbtn-neutral-h)",
    "--active-bg":   "var(--tbtn-neutral-act)",
  },
  danger: {
    color: "var(--tbtn-danger)",
    "--hover-bg":    "var(--tbtn-danger-bg)",
    "--hover-color": "var(--tbtn-danger-h)",
    "--active-bg":   "var(--tbtn-danger-act)",
  },
  success: {
    color: "var(--tbtn-success)",
    "--hover-bg":    "var(--tbtn-success-bg)",
    "--hover-color": "var(--tbtn-success-h)",
    "--active-bg":   "transparent",
  },
};

const sizeStyles: Record<TextButtonSize, React.CSSProperties> = {
  sm: { fontSize: "12px", padding: "4px 6px",   gap: "4px" },
  md: { fontSize: "14px", padding: "6px 8px",   gap: "5px" },
  lg: { fontSize: "16px", padding: "8px 10px",  gap: "6px" },
};

const iconSizes: Record<TextButtonSize, number> = {
  sm: 12,
  md: 14,
  lg: 16,
};

// ─── Spinner ──────────────────────────────────────────────────────────────────

const spinnerStyle: React.CSSProperties = {
  width:  12,
  height: 12,
  border: "1.5px solid currentColor",
  borderTopColor: "transparent",
  borderRadius: "50%",
  animation: "tbtn-spin .6s linear infinite",
  flexShrink: 0,
};

const Spinner = () => <span style={spinnerStyle} aria-hidden="true" />;

// ─── Component ────────────────────────────────────────────────────────────────

export const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps>(
  (
    {
      intent   = "primary",
      size     = "md",
      iconLeft,
      iconRight,
      loading  = false,
      disabled,
      children,
      style,
      onMouseEnter,
      onMouseLeave,
      onMouseDown,
      onMouseUp,
      ...props
    },
    ref
  ) => {
    const [hovered, setHovered] = React.useState(false);
    const [active,  setActive]  = React.useState(false);

    const isDisabled = disabled || loading;
    const iProps     = intentStyles[intent];

    const baseStyle: React.CSSProperties = {
      display:        "inline-flex",
      alignItems:     "center",
      justifyContent: "center",
      fontFamily:     "var(--font-ui, 'Inter', sans-serif)",
      fontWeight:     500,
      lineHeight:     1,
      border:         "none",
      borderRadius:   "5px",
      cursor:         isDisabled ? (loading ? "wait" : "not-allowed") : "pointer",
      whiteSpace:     "nowrap",
      outline:        "none",
      transition:     "background 130ms, color 130ms",
      opacity:        isDisabled ? (loading ? 0.7 : 0.38) : 1,
      pointerEvents:  isDisabled ? "none" : "auto",
      background:     active
                        ? iProps["--active-bg"]
                        : hovered
                          ? iProps["--hover-bg"]
                          : "transparent",
      color:          hovered && !active ? iProps["--hover-color"] : iProps.color,
      ...sizeStyles[size],
      ...style,
    };

    const iconWrap = (icon: React.ReactNode) => (
      <span
        style={{
          display:        "inline-flex",
          alignItems:     "center",
          justifyContent: "center",
          flexShrink:     0,
          width:          iconSizes[size],
          height:         iconSizes[size],
        }}
        aria-hidden="true"
      >
        {icon}
      </span>
    );

    return (
      <>
        {/* Inject keyframes once */}
        <style>{`
          @keyframes tbtn-spin { to { transform: rotate(360deg); } }
          .tbtn-focus:focus-visible { box-shadow: 0 0 0 2px var(--tbtn-primary, #1B6EF3); }
        `}</style>

        <button
          ref={ref}
          className="tbtn-focus"
          disabled={isDisabled}
          aria-disabled={isDisabled}
          aria-busy={loading}
          style={baseStyle}
          onMouseEnter={(e) => { setHovered(true);  onMouseEnter?.(e); }}
          onMouseLeave={(e) => { setHovered(false); setActive(false); onMouseLeave?.(e); }}
          onMouseDown={(e)  => { setActive(true);   onMouseDown?.(e); }}
          onMouseUp={(e)    => { setActive(false);  onMouseUp?.(e);   }}
          {...props}
        >
          {loading
            ? <Spinner />
            : iconLeft && iconWrap(iconLeft)
          }

          {children && <span>{children}</span>}

          {!loading && iconRight && iconWrap(iconRight)}
        </button>
      </>
    );
  }
);

TextButton.displayName = "TextButton";

export default TextButton;

